import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { EmberGlow } from "./EmberGlow";
import { LogeEvent } from "../../types/entities";

// Fine film grain as a data URI, so it costs no extra request. Kept at very
// low opacity and blended over the ink — it should register as texture on a
// printed page, not as visible noise.
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const pad = (n: number) => String(Math.max(0, n)).padStart(2, "0");

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /**
   * Whole calendar days between today and the event's date — how many sleeps
   * away it is. Deliberately separate from `days` above, which counts elapsed
   * 24-hour periods: at 23:00, an event tomorrow at 18:00 is 0 elapsed days
   * away but 1 calendar day away, and calling that "today" is wrong.
   */
  calendarDaysAway: number;
  /** The start time has passed — the gathering is under way (or done). */
  hasStarted: boolean;
}

const remainingUntil = (target: string): Remaining => {
  const now = dayjs();
  const totalSec = Math.max(0, Math.floor((dayjs(target).valueOf() - now.valueOf()) / 1000));
  return {
    days: Math.floor(totalSec / 86400),
    hours: Math.floor((totalSec % 86400) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
    calendarDaysAway: dayjs(target).startOf("day").diff(now.startOf("day"), "day"),
    hasStarted: dayjs(target).valueOf() <= now.valueOf(),
  };
};

/**
 * Ticks once a second. Lives in this component rather than the page so the
 * event list doesn't re-render alongside the clock.
 */
const useCountdown = (target: string): Remaining => {
  const [left, setLeft] = useState(() => remainingUntil(target));
  useEffect(() => {
    setLeft(remainingUntil(target));
    const id = setInterval(() => setLeft(remainingUntil(target)), 1000);
    return () => clearInterval(id);
  }, [target]);
  return left;
};

const Unit = ({ value, label }: { value: number; label: string }) => (
  <div className="flex flex-col items-start">
    <div className="font-display font-extrabold text-[22px] leading-none tracking-tight tabular-nums">
      {pad(value)}
    </div>
    <div className="font-mono text-[8.5px] tracking-[1.3px] text-white/45 uppercase mt-1.5">
      {label}
    </div>
  </div>
);

const Separator = () => (
  <div className="font-display font-extrabold text-[22px] leading-none text-white/25 -mt-0.5">:</div>
);

const CountdownBanner = ({
  event,
  onClick,
}: {
  event: LogeEvent;
  /** Opens the samling this is counting down to. Router-agnostic on
   * purpose — the caller already knows how to navigate, this card doesn't
   * need to. */
  onClick?: () => void;
}) => {
  const { days, hours, minutes, seconds, calendarDaysAway, hasStarted } = useCountdown(event.date);
  // Under 24 hours out, a "0 dage" hero reads as broken, so name the day and
  // let the clock below carry the urgency. Which word it is has to come from
  // the calendar, not the elapsed time: late in the evening, an event the
  // next morning is under 24 hours away but is still tomorrow.
  const closeEnoughToName = days === 0;
  const dayName = calendarDaysAway <= 0 ? "I dag" : "I morgen";

  return (
    // `dss-ticking` isolates this box from the rest of the page's layout. The
    // clock rewrites itself once a second, and on iOS a DOM change inside a
    // scrolling container can cut a scroll's momentum dead — which is what it
    // looks like when the calendar refuses to travel any further up.
    //
    // The whole card opens the samling it's counting down to — same "tap
    // anywhere on the row" convention as a calendar entry below it.
    <div
      onClick={onClick}
      className={`dss-ticking relative overflow-hidden rounded-lg bg-ink text-paper ${
        onClick ? "cursor-pointer active:opacity-90" : ""
      }`}
    >
      {/* Warm ember glow — a campfire read that suits a scout corps better
          than a cool one, and gives the flat ink card some depth. Lightly
          alive: see EmberGlow for why it's two independent layers rather
          than one breathing gradient. */}
      <EmberGlow
        ember="radial-gradient(230% 170% at 10% 8%, rgba(166,126,68,0.34) 0%, rgba(166,126,68,0.10) 38%, transparent 68%)"
        corner="radial-gradient(180% 140% at 100% 100%, rgba(26,72,36,0.38) 0%, transparent 62%)"
        borderRadius="rounded-lg"
      />

      {/* The event's emoji as an editorial watermark bleeding off the corner,
          rather than a small glyph pinned in the margin. */}
      <div
        aria-hidden
        className="absolute -right-6 -bottom-8 text-[132px] leading-none opacity-[0.09] select-none pointer-events-none"
      >
        {event.emoji}
      </div>

      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.055] mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: GRAIN }}
      />

      <div className="relative px-5 pt-4 pb-[18px]">
        {/* The countdown reads as a pile of loose numerals to a screen reader,
            and re-announcing it every second would be unusable — so state it
            once, in prose, and hide the ticking digits below. */}
        <span className="sr-only">
          {`Næste samling: ${event.title}, ${dayjs(event.date).format("D. MMMM [kl.] HH:mm")}. ` +
            (hasStarted ? `${dayName} — allerede begyndt.` : closeEnoughToName ? `${dayName}.` : `Om ${days} ${days === 1 ? "dag" : "dage"}.`)}
        </span>

        <div aria-hidden>
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] tracking-[1.4px] text-white/55 uppercase">
              Næste samling
            </div>
            <div className="font-mono text-[10px] tracking-[1.4px] text-white/40 uppercase">
              {dayjs(event.date).format("D. MMM")}
            </div>
          </div>

          {/* Fixed height so the "I dag" wording and the day count occupy the
              same space — otherwise the card resizes on the day of an event,
              and the loading skeleton can only match one of the two. */}
          <div className="mt-3 h-[61px] flex items-end">
            {closeEnoughToName ? (
              <div className="font-display font-extrabold text-[58px] leading-[0.9] tracking-tighter uppercase">
                {dayName}
              </div>
            ) : (
              <div className="flex items-baseline gap-2.5">
                <div className="font-display font-extrabold text-[78px] leading-[0.78] tracking-tighter tabular-nums">
                  {days}
                </div>
                <div className="font-mono text-[11px] tracking-[1.6px] text-white/55 uppercase pb-1.5">
                  {days === 1 ? "dag" : "dage"}
                </div>
              </div>
            )}
          </div>

          <div className="font-display font-bold text-[19px] leading-tight tracking-tight mt-2 uppercase break-words">
            {event.title}
          </div>

          <div className="h-px bg-white/15 mt-3.5" />

          {/* Pinned height for the same reason as the hero: once the gathering
              starts there is nothing left to count down, and a row of zeroes
              reads as a broken clock — so it's replaced rather than frozen,
              without resizing the card. */}
          <div className="mt-3 h-[41px] flex items-start">
            {hasStarted ? (
              <div className="font-mono text-[11px] tracking-[1.4px] text-white/60 uppercase pt-1">
                Startet kl. {dayjs(event.date).format("HH.mm")}
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <Unit value={hours} label="timer" />
                <Separator />
                <Unit value={minutes} label="min" />
                <Separator />
                <Unit value={seconds} label="sek" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountdownBanner;
